// components/entities/LegalEntityRenderer.tsx
import React, { memo, useCallback } from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import { AlertTriangle, Building2, Calendar, Fingerprint, MapPin, TrendingUp } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import { useKernelStore } from '@/kernel/kernelStores';
import { BaseEntityProps, EntityRowAction, LeasingLegalEntityDTO } from '#types/types';
import { getScoreColorClass, portalTableRow } from '@/components/portal/portalTheme';
import { EntitySkeleton } from './EntityView';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css'; // IMPORTANT
import { EntityRowActions } from './EntityRowActions';

const CELL_ALIGN_START: React.CSSProperties = { justifyContent: 'flex-start' };
const CELL_ALIGN_END: React.CSSProperties = { justifyContent: 'flex-end' };

export interface LegalEntityRendererProps extends BaseEntityProps<LeasingLegalEntityDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingLegalEntityDTO>[];
}

export const LegalEntityRenderer = memo(({
  entityId,
  variant = 'row',
  onClick,
  className,
  columns,
  rowActions = [],
}: LegalEntityRendererProps) => {
  const data = useKernelStore((state) => state.inventory.LegalEntity.registry[entityId]);

  const isFrench = (data?.country ?? '').toUpperCase() === 'FR';
  const registrationNumber = data?.registrationNumber;

  const handleOpenRegistry = useCallback((e: React.MouseEvent) => {
    if (!isFrench || !registrationNumber) return;
    e.stopPropagation();
    const url = `https://annuaire-entreprises.data.gouv.fr/rechercher?terme=${encodeURIComponent(registrationNumber)}`;
    window.open(url, '_blank');
  }, [isFrench, registrationNumber]);

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  if (variant === 'row') {
    const cellRenderers: Record<string, React.ReactNode> = {
      legalName: (
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white/5 border border-white/5">
            <Building2 size={14} className="text-white/40" />
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-white">{data.legalName}</span>
            <span className="truncate text-[10px] text-white/40">{data.status || 'Active'}</span>
          </div>
        </div>
      ),
      registration: (
        <div 
          className={clsx("group flex min-w-0 items-center gap-2", isFrench ? "cursor-pointer" : "opacity-60")}
          onClick={handleOpenRegistry}
        >
          <Fingerprint size={14} className="shrink-0 text-emerald-400" />
          <span className="font-mono text-xs text-white/70 group-hover:text-white group-hover:underline">
            {data.registrationNumber}
          </span>
        </div>
      ),
      headquarters: (
        <div className="flex min-w-0 items-center gap-2 text-white/60">
          <MapPin size={14} className="shrink-0 opacity-50" />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-xs">{data.city || '-'}</span>
            <span className="truncate text-[10px] opacity-50">{data.postalCode}</span>
          </div>
        </div>
      ),
      brands: (
        <div className="flex min-w-0 items-center gap-2">
          {data.linkedBrands?.slice(0, 3).map((brand) => (
            <div
              key={brand.id}
              className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5"
              title={brand.name}
            >
              {brand.logoUrl ? (
                <BrandMark logoUrl={brand.logoUrl} name={brand.name} size={30} radius={7} />
              ) : (
                <span className="text-[10px] font-bold text-white/40">{brand.name.substring(0, 2).toUpperCase()}</span>
              )}
            </div>
          ))}
        </div>
      ),
      score: (
        <div className="flex items-center">
          {data.financialScore !== null ? (
            <div className="flex items-baseline gap-1">
              <span className={clsx('text-sm font-bold', getScoreColorClass(data.financialScore))}>{data.financialScore}</span>
              <span className="text-[10px] text-white/30">/100</span>
            </div>
          ) : <span className="text-xs text-white/20">-</span>}
        </div>
      ),
      status: (
        <div className="flex items-center gap-1">
          {data.financialStatus ? (
            <span className={clsx('rounded border px-1.5 py-0.5 text-[9px]',
              data.financialStatus === 'STRONG' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              : data.financialStatus === 'DISTRESSED' ? 'bg-red-500/15 text-red-400 border-red-500/30'
              : data.financialStatus === 'WATCHLIST' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
              : 'bg-white/5 text-white/50 border-white/10'
            )}>
              {data.financialStatus}
            </span>
          ) : <span className="text-xs text-white/20">-</span>}
          {/* Non-eliminating alert on the financial criterion (leasing keeps the brand and decides ,
              a weak/absent score never removes it; only liquidation does). 2026-07-18 leasing call. */}
          {data.financialStatus === 'DISTRESSED' || data.financialStatus === 'WATCHLIST' ? (
            <AlertTriangle size={11} className={data.financialStatus === 'DISTRESSED' ? 'text-red-400' : 'text-amber-400'} />
          ) : null}
        </div>
      ),
      growth: (
        <div className="flex items-center">
          {data.revenueGrowth !== null ? (
            <div className={clsx('flex items-center gap-1 text-xs font-mono', data.revenueGrowth > 0 ? 'text-emerald-400' : 'text-red-400')}>
              <TrendingUp size={12} className={data.revenueGrowth < 0 ? '-scale-y-100' : ''} />
              {data.revenueGrowth > 0 ? '+' : ''}{data.revenueGrowth.toFixed(1)}%
            </div>
          ) : <span className="text-xs text-white/20">-</span>}
        </div>
      ),
      founded: (
        <div className="flex items-center justify-end gap-2 text-white/40">
          {data.registrationDate ? (
            <>
              <span className="text-xs">{new Date(data.registrationDate).getFullYear()}</span>
              <Calendar size={12} />
            </>
          ) : <span className="text-xs">-</span>}
        </div>
      ),
      actions: (
        <div className="h-full flex items-center justify-end w-full">
          <EntityRowActions entityType="LegalEntity" entity={data} rowActions={rowActions} />
        </div>
      ),
    };

    const activeColumns = columns || [];

    return (
      <div
        className={clsx(
          portalTableRow, 
          styles.gridRow, 
          styles.bodyRow, 
          'group h-12 w-full cursor-pointer border-b border-[#2A2A2A] bg-[#181818] hover:bg-[#1E1E1E]', 
          className
        )}
        onClick={onClick}
      >
        {activeColumns.map((column) => (
          <div 
            key={column.id} 
            className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)} 
            style={column.id === 'actions' || column.align === 'right' ? CELL_ALIGN_END : CELL_ALIGN_START}
          >
            {cellRenderers[column.id] || null}
          </div>
        ))}
      </div>
    );
  }

  return null;
});

LegalEntityRenderer.displayName = 'LegalEntityRenderer';
